import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import ClarityOneLayout from "@/components/clarity-one/ClarityOneLayout";
import TopUpWalletFlow from "@/components/clarity-one/TopUpWalletFlow";
import { clarityOneLoginUrlWithRedirect } from "@shared/clarityOneAuthRedirect";
import { formatTotalsTrackedSinceDisclaimer } from "@/lib/clarityOneFormat";
import { formatDate } from "@/lib/dateUtils";
import { topUpCustomerStatusMessage, topUpStatusLabel } from "@shared/kaleidescapeTopUp";
import { toast } from "sonner";
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronLeft,
  Clock,
  CreditCard,
  Info,
  Loader2,
  Pencil,
  Wallet,
  Check,
  X,
  AlertTriangle,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NOTES_PLACEHOLDER =
  "Enter movie title purchased, add a note, or anything you'd like to remember.";

function formatUsd(amount: string | null | undefined): string {
  const n = parseFloat(amount ?? "0");
  return `US$${Number.isFinite(n) ? n.toFixed(2) : "0.00"}`;
}

function formatWalletDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return formatDate(value);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-SG", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function StatCard({
  icon: Icon,
  label,
  value,
  disclaimer,
}: {
  icon: typeof Wallet;
  label: string;
  value: string;
  disclaimer?: string;
}) {
  return (
    <div className="bg-white rounded-xl border border-[#E8E4DC] p-4 sm:p-5 flex flex-col min-h-[120px]">
      <Icon className="h-4 w-4 text-[#8B7355] mb-3" strokeWidth={1.5} />
      <p className="text-[10px] sm:text-[11px] tracking-[0.12em] uppercase text-[#8B7355] mb-2">{label}</p>
      <p className="text-lg sm:text-xl font-light text-[#1A2744] tracking-tight">{value}</p>
      {disclaimer ? (
        <p className="mt-auto pt-2 text-[9px] tracking-[0.1em] uppercase text-[#8B7355]/80 flex items-center gap-1">
          {disclaimer}
          <Info className="h-3 w-3 shrink-0" aria-hidden />
        </p>
      ) : null}
    </div>
  );
}

export default function KaleidescapeWalletPage() {
  const [, setLocation] = useLocation();
  const [topUpOpen, setTopUpOpen] = useState(false);
  const { data: me, isLoading: meLoading } = trpc.clarityOneAuth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });

  const walletEnabled = Boolean(me?.kaleidescapeWalletEnabled);

  const { data, isLoading, refetch } = trpc.kaleidescapeWallet.customerGetWallet.useQuery(undefined, {
    enabled: !!me && walletEnabled,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    if (!meLoading && !me) {
      setLocation(clarityOneLoginUrlWithRedirect("/wallet/kaleidescape"));
    }
  }, [meLoading, me, setLocation]);

  useEffect(() => {
    if (!meLoading && me && !walletEnabled) {
      setLocation("/home");
    }
  }, [meLoading, me, walletEnabled, setLocation]);

  if (meLoading || !me || !walletEnabled) {
    return (
      <ClarityOneLayout showNav={false}>
        <div className="flex justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#B8956E]" />
        </div>
      </ClarityOneLayout>
    );
  }

  const wallet = data?.wallet;
  const transactions = data?.transactions ?? [];
  const topUpRequests = data?.topUpRequests ?? [];
  const lowBalance = data?.lowBalance ?? false;
  const topUpSettings = data?.topUpSettings;
  const sinceDisclaimer = wallet?.totalsTrackedSince
    ? formatTotalsTrackedSinceDisclaimer(wallet.totalsTrackedSince)
    : null;

  return (
    <ClarityOneLayout showNav>
      <div className="mb-8 flex items-center gap-4">
        <Link
          href="/home"
          className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] uppercase text-[#8B7355] hover:text-[#1A2744] no-underline"
        >
          <ChevronLeft className="h-4 w-4" />
          Home
        </Link>
      </div>

      <div className="text-center mb-8 sm:mb-10">
        <p className="text-[11px] tracking-[0.2em] uppercase text-[#B8956E]">Kaleidescape Wallet</p>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-7 w-7 animate-spin text-[#B8956E]" />
        </div>
      ) : !wallet ? (
        <div className="bg-white rounded-2xl border border-[#E8E4DC] p-10 text-center max-w-lg mx-auto">
          <p className="text-[#8B7355]">Your Kaleidescape Wallet has not been set up yet.</p>
          <p className="text-sm text-[#8B7355]/70 mt-2">Please contact Clarity for assistance.</p>
        </div>
      ) : (
        <>
          {lowBalance && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50/80 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-900">Low balance warning</p>
                  <p className="text-sm text-amber-800/90 mt-1 leading-relaxed">
                    Your Kaleidescape Wallet balance is below USD{" "}
                    {topUpSettings?.lowBalanceThresholdUsd ?? 20}. You may wish to top up to continue
                    purchasing movies.
                  </p>
                </div>
              </div>
              <Button
                className="bg-[#1A2744] hover:bg-[#243556] shrink-0"
                onClick={() => setTopUpOpen(true)}
              >
                Top Up Wallet
              </Button>
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div />
            {!lowBalance && (
              <Button
                className="bg-[#1A2744] hover:bg-[#243556] w-full sm:w-auto"
                onClick={() => setTopUpOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Top Up Wallet
              </Button>
            )}
          </div>

          {topUpSettings && (
            <TopUpWalletFlow
              open={topUpOpen}
              onOpenChange={setTopUpOpen}
              exchangeRate={topUpSettings.usdSgdRate}
              adminFeePercent={topUpSettings.adminFeePercent}
              onComplete={() => void refetch()}
            />
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-10">
            <StatCard icon={Wallet} label="Current Balance" value={formatUsd(wallet.currentBalance)} />
            <StatCard
              icon={ArrowUpRight}
              label="Total Topped Up"
              value={formatUsd(wallet.totalToppedUp)}
              disclaimer={sinceDisclaimer ?? undefined}
            />
            <StatCard
              icon={ArrowDownLeft}
              label="Total Spent"
              value={formatUsd(wallet.totalSpent)}
              disclaimer={sinceDisclaimer ?? undefined}
            />
            <StatCard
              icon={CreditCard}
              label="Card Ending"
              value={wallet.cardLast4 ? `•••• ${wallet.cardLast4}` : "—"}
            />
            <StatCard
              icon={Clock}
              label="Last Updated"
              value={wallet.lastUpdatedAt ? formatWalletDateTime(wallet.lastUpdatedAt).split(",")[0] ?? "—" : "—"}
            />
          </div>

          {topUpRequests.length > 0 && (
            <section className="mb-10">
              <h2 className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#B8956E] mb-4">
                Top-Up Requests
              </h2>
              <div className="bg-white rounded-2xl border border-[#E8E4DC] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] text-sm">
                    <thead>
                      <tr className="border-b border-[#E8E4DC] bg-[#FAFAF7]/80">
                        {["Requested", "USD", "Payable SGD", "Status", "Date"].map((h) => (
                          <th
                            key={h}
                            className="text-left text-[10px] tracking-[0.12em] uppercase text-[#8B7355] font-medium px-4 py-3 first:pl-5"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {topUpRequests.map((req) => (
                        <tr key={req.id} className="border-b border-[#F0EDE6] last:border-0">
                          <td className="px-4 py-3 pl-5 text-[#8B7355]">#{req.id}</td>
                          <td className="px-4 py-3">{formatUsd(req.requestedUsdAmount)}</td>
                          <td className="px-4 py-3">S${parseFloat(req.payableSgdAmount).toFixed(2)}</td>
                          <td className="px-4 py-3">
                            <div className="space-y-1">
                              <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-[#FAFAF7] text-[#1A2744] border border-[#E8E4DC]">
                                {topUpStatusLabel(req.status)}
                              </span>
                              {topUpCustomerStatusMessage(req.status) && (
                                <p className="text-xs text-[#8B7355] max-w-xs leading-relaxed">
                                  {topUpCustomerStatusMessage(req.status)}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[#8B7355] text-xs whitespace-nowrap">
                            {formatWalletDateTime(req.requestedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#B8956E]">
                Transaction History
              </h2>
            </div>

            {transactions.length === 0 ? (
              <p className="text-sm text-[#8B7355] py-8 text-center">No transactions yet.</p>
            ) : (
              <div className="bg-white rounded-2xl border border-[#E8E4DC] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr className="border-b border-[#E8E4DC] bg-[#FAFAF7]/80">
                        {["Date", "Type", "Description", "Amount", "My Notes"].map((h) => (
                          <th
                            key={h}
                            className="text-left text-[10px] tracking-[0.12em] uppercase text-[#8B7355] font-medium px-4 py-3 first:pl-5 last:pr-5"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <TransactionRow key={tx.id} tx={tx} onSaved={() => void refetch()} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          <p className="mt-8 text-xs text-[#8B7355]/80 leading-relaxed max-w-2xl">
            Amounts are shown in USD. {sinceDisclaimer ? `${sinceDisclaimer.replace("SINCE ", "Totals are tracked since ")}.` : ""}{" "}
            Historical wallet activity prior to Clarity One launch may not appear here.
          </p>
        </>
      )}
    </ClarityOneLayout>
  );
}

type TxRow = {
  id: number;
  type: "add_money" | "spend_money" | "merge_transfer_in" | "merge_transfer_out";
  amount: string | null;
  transactionDate: string;
  remarks: string | null;
  customerNotes: string | null;
};

function TransactionRow({ tx, onSaved }: { tx: TxRow; onSaved: () => void }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(tx.customerNotes ?? "");

  const updateNotes = trpc.kaleidescapeWallet.customerUpdateNotes.useMutation({
    onSuccess: () => {
      toast.success("Note saved");
      setEditing(false);
      onSaved();
    },
    onError: (err) => toast.error(err.message),
  });

  const isMerge = tx.type === "merge_transfer_in" || tx.type === "merge_transfer_out";
  const isCredit = tx.type === "add_money" || tx.type === "merge_transfer_in";
  const amountLabel = `${isCredit ? "+" : "−"}${formatUsd(tx.amount)}`;
  const typeLabel = isMerge
    ? "Admin reconcile"
    : isCredit
      ? "Top Up"
      : "Purchase";
  const typeClass = isMerge
    ? "bg-slate-100 text-slate-700"
    : isCredit
      ? "bg-emerald-50 text-emerald-800"
      : "bg-red-50 text-red-800/90";

  return (
    <tr className="border-b border-[#F0EDE6] last:border-0 align-top">
      <td className="px-4 py-4 pl-5 text-[#8B7355] whitespace-nowrap text-xs">
        {formatDate(tx.transactionDate)}
      </td>
      <td className="px-4 py-4">
        <span className={`inline-block text-[10px] tracking-wide uppercase px-2 py-0.5 rounded ${typeClass}`}>
          {typeLabel}
        </span>
      </td>
      <td className="px-4 py-4 text-[#1A2744] max-w-[180px]">
        <span className="line-clamp-2">
          {tx.remarks ||
            (isMerge
              ? "Administrative wallet reconciliation"
              : isCredit
                ? "Top up"
                : "Purchase")}
        </span>
      </td>
      <td
        className={`px-4 py-4 font-medium whitespace-nowrap ${
          isMerge ? "text-slate-700" : isCredit ? "text-emerald-700" : "text-[#1A2744]"
        }`}
      >
        {amountLabel}
      </td>
      <td className="px-4 py-4 pr-5 min-w-[200px]">
        {editing ? (
          <div className="space-y-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={NOTES_PLACEHOLDER}
              className="w-full text-xs min-h-[72px] border border-[#E8E4DC] rounded-lg p-2 resize-none bg-[#FAFAF7] focus:outline-none focus:ring-1 focus:ring-[#B8956E]"
              maxLength={2000}
            />
            <div className="flex gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs bg-[#1A2744] text-white px-3 py-1.5 rounded-md disabled:opacity-50"
                disabled={updateNotes.isPending}
                onClick={() => updateNotes.mutate({ transactionId: tx.id, customerNotes: notes || null })}
              >
                <Check className="h-3 w-3" /> Save
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs text-[#8B7355] px-2 py-1.5"
                onClick={() => setEditing(false)}
              >
                <X className="h-3 w-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-2">
            <p className="text-xs text-[#8B7355]/90 leading-relaxed flex-1 italic">
              {tx.customerNotes || (
                <span className="not-italic text-[#8B7355]/50">{NOTES_PLACEHOLDER}</span>
              )}
            </p>
            <button
              type="button"
              className="text-[#B8956E] hover:text-[#1A2744] p-1 shrink-0"
              onClick={() => {
                setNotes(tx.customerNotes ?? "");
                setEditing(true);
              }}
              aria-label="Edit note"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}
